// Singleton manager to prevent duplicate entitlements fetches
class EntitlementsFetchManager {
  private static instance: EntitlementsFetchManager;
  private currentFetch: Promise<any> | null = null;
  private lastFetchTime: number = 0;
  private lastFetchParams: string = '';
  private readonly CACHE_DURATION = 1000; // 1 second cache to prevent rapid re-fetches

  static getInstance(): EntitlementsFetchManager {
    if (!EntitlementsFetchManager.instance) {
      EntitlementsFetchManager.instance = new EntitlementsFetchManager();
    }
    return EntitlementsFetchManager.instance;
  }

  async fetchEntitlements(
    fetchFn: () => Promise<any>,
    paramsKey: string
  ): Promise<any> {
    const now = Date.now();
    
    // If we have a recent fetch with the same params, return the existing promise
    if (
      this.currentFetch &&
      this.lastFetchParams === paramsKey &&
      now - this.lastFetchTime < this.CACHE_DURATION
    ) {
      return this.currentFetch;
    }

    // If a fetch is in progress but with different params, wait for it to complete
    if (this.currentFetch && this.lastFetchParams !== paramsKey) {
      try {
        await this.currentFetch;
      } catch {
        // Ignore errors from previous fetch
      }
    }
    this.lastFetchTime = now;
    this.lastFetchParams = paramsKey;
    
    this.currentFetch = fetchFn()
      .then(result => {
        // Keep the promise cached for a short duration
        setTimeout(() => {
          if (this.currentFetch && this.lastFetchParams === paramsKey) {
            this.currentFetch = null;
          }
        }, this.CACHE_DURATION);
        return result;
      })
      .catch(error => {
        // Clear on error
        this.currentFetch = null;
        throw error;
      });

    return this.currentFetch;
  }

  clearCache() {
    this.currentFetch = null;
    this.lastFetchTime = 0;
    this.lastFetchParams = '';
  }
}

export const entitlementsFetchManager = EntitlementsFetchManager.getInstance();